import { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import moment from 'moment';
import { getWeekdays } from '~/utils/helpers';
import WorkingHoursSlots from '~/view/screens/BranchesManagementScreen/screens/BranchesWorkingHours/components/TimeSlots/TimeSlots';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import { localeStore } from '~/mobx/localesStore';
import ScrollableBlock from '~/view/containers/ScrollableBlock/ScrollableBlock';
import { branchesWorkingHoursStore } from '~/view/screens/BranchesManagementScreen/screens/BranchesWorkingHours/store/branchesWorkingHoursStore';
import { WORKING_HOURS_ORDER_TYPE } from '~/view/screens/BranchesManagementScreen/screens/BranchEditScreen/types/branchEdit';
import { TBranchWorkingTime } from '~/view/screens/BranchesManagementScreen/screens/BranchesWorkingHours/types';
import { getNextDayOfWeek } from '~/helpers/getNextDayOfWeek/getNextDayOfWeek';
import './style.scss';
import LoadingOverlay from '~/view/components/LoadingOverlay/LoadingOverlay';
import { LoadingStatus } from '~/types/main';
import integrationsStore from '~/mobx/integrationStore';
import LogLink from '~/view/components/LogLink';
import { SCREENS } from '~/constants/screens';

const ProfileWorkingHours = observer(() => {
  const { selectedProfile, isProfileCreatedInFoodics } = branchesWorkingHoursStore;
  const [timeSlots, setTimeSlots] = useState<TBranchWorkingTime[]>(selectedProfile?.workingTime || []);

  const isProfileNotEditable = useMemo((): boolean => {
    return branchesWorkingHoursStore?.isBlockedByDeliverect ? false : branchesWorkingHoursStore?.isProfileNotEditable;
  }, [branchesWorkingHoursStore?.isProfileNotEditable, branchesWorkingHoursStore?.isBlockedByDeliverect]);

  const restrictionMessage = isProfileNotEditable ? (isProfileCreatedInFoodics ? 'changes_in_Foodics' : 'please_clone_the_profile') : null;

  const handleTimeSlotSaving = (
    weekday: string,
    id: number | undefined,
    startTime: Date,
    closeTime: Date,
    menuGroupId: number | undefined,
    menuGroupName: string | undefined,
    orderType: WORKING_HOURS_ORDER_TYPE | undefined
  ) => {
    const currDaySlot = {
      weekday,
      id: id ?? Date.now(),
      startTime: moment(startTime).format('HH:mm:ss'),
      closeTime: moment(closeTime).isBefore(startTime) ? '23:59:59' : moment(closeTime).format('HH:mm:ss'),
      menuGroupId,
      menuGroupName,
      orderType,
    };

    // handle current day slots
    if (id !== undefined) {
      setTimeSlots((prevSlots) => prevSlots.map((slot) => (slot.id === id ? { ...slot, ...currDaySlot } : slot)));
    } else {
      setTimeSlots((prevSlots) => [...prevSlots, { ...currDaySlot, isNew: true }]);
    }

    // handle next day slot
    if (moment(closeTime).isBefore(startTime)) {
      const nextDaySlot = {
        ...currDaySlot,
        weekday: getNextDayOfWeek(weekday),
        id: Date.now(),
        startTime: '00:00:00',
        closeTime: moment(closeTime).format('HH:mm:ss'),
        isNew: true,
      };

      setTimeSlots((prevSlots) => [...prevSlots, nextDaySlot]);
    }

    branchesWorkingHoursStore.setUnsavedFlag(true);
  };

  const handleTimeSlotRemoval = (timeSlotId: number | undefined) => {
    setTimeSlots((prevSlots) => prevSlots.filter((slot) => slot.id !== timeSlotId));
    branchesWorkingHoursStore.setUnsavedFlag(true);
  };

  const handleUpdateProfileTimeSlots = async () => {
    const updatedTimeSlots = await branchesWorkingHoursStore.updateProfileTimeSlots(timeSlots);
    if (updatedTimeSlots) setTimeSlots(updatedTimeSlots);
  };

  if (!selectedProfile) return null;

  return (
    <div className="d-flex flex-1 flex-column">
      {restrictionMessage && <p className="text-size-16 ml-4 mb-4">{localeStore.t(restrictionMessage)}</p>}
      <div className="slots ml-4">
        <ScrollableBlock
          scrollClassName={classNames('slots__days-wrapper', { slots_disabled: isProfileNotEditable && !isProfileCreatedInFoodics })}
          showScrollbar
          scrollbarSize="small"
        >
          {getWeekdays().map((weekday, idx) => (
            <WorkingHoursSlots
              key={idx}
              day={weekday}
              workingTime={timeSlots}
              disabled={isProfileNotEditable}
              posIntAccess={integrationsStore.posRestrictions?.branchWorkingHours}
              onSave={handleTimeSlotSaving}
              onDelete={handleTimeSlotRemoval}
            />
          ))}
        </ScrollableBlock>
        <Button title={localeStore.t('Save')} onClick={handleUpdateProfileTimeSlots} />
        <LogLink link={SCREENS.BRANCHES_WORKING_HOURS_LOG(selectedProfile.id)} />
      </div>
      <LoadingOverlay visible={branchesWorkingHoursStore.loadingStatus === LoadingStatus.Loading} refreshing />
    </div>
  );
});

export default ProfileWorkingHours;
